import "./style.css";
import { useEffect, useState } from "react";
import { Button } from "components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { seatSizes } from "utils";
import UserIcon from "assets/icons/user.svg";
import MailIcon from "assets/icons/mail.svg";
import LockIcon from "assets/icons/lock.svg";
import {
  autoSignIn,
  confirmSignUp,
  resendSignUpCode,
  signUp,
} from "aws-amplify/auth";
import ConfirmSignUpForm from "../ConfirmSignUpForm";
import { useAuthStore } from "stores/auth/authStore";
import { ClientPhoneIcon } from "components/Icons";
import ModalStore from "components/Modal/ModalStore";
import { Modal, useModalStore } from "stores/modal/useModalStore";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "components/ui/input";
import { PasswordInput } from "components/ui/password-input";
import { Loader2 } from "lucide-react";
import MultiSelectField from "components/Checkbox/HorseProfile/HorseMultiSelect";

const schema = z.object({
  size: z.string().min(2, { message: "Seat size is required" }),
  given_name: z.string().min(1, { message: "First name is required" }),
  family_name: z.string().min(1, { message: "Last name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  number: z.string().min(1, { message: "Phone number is required" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters long" }),
  repeatpassword: z
    .string()
    .min(8, { message: "Password must be at least 8 characters long" }),
});
const codeSchema = z.object({
  prompt_code: z.string().min(1, { message: "Code is required" }),
});

const dynamicSchema = (newUser) => {
  if (newUser) {
    return codeSchema;
  }
  return schema;
};

const RegisterForm = () => {
  const open = useModalStore((s) => s.open);
  const close = useModalStore((s) => s.close);

  const navigate = useNavigate();
  const { setIsAction, fetchFullUserProfile } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [email, setEmail] = useState(null);
  const [countdown, setCountdown] = useState(120);
  const [showCountDown, setShowCountDown] = useState(true);
  const form = useForm({
    mode: "onBlur",
    resolver: zodResolver(dynamicSchema(newUser)),
    defaultValues: {
      size: "",
      given_name: "",
      family_name: "",
      email: "",
      number: "+1",
      password: "",
      repeatpassword: "",
      prompt_code: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const sizeValue = form.watch("size");

  const selectedSizes = sizeValue
    ? sizeValue
      .split(",")
      .map((value) => {
        return seatSizes.find((option) => option.value.toString() === value);
      })
      .filter(Boolean)
    : [];

  const handlePhoneNumberChange = (event) => {
    let value = event.target.value.replace(/\D/g, "");

    if (value.startsWith("1")) {
      value = value.slice(1);
    }

    if (value.length > 10) {
      value = value.slice(0, 10);
    }

    if (value.length > 6) {
      value = `+1 (${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(
        6
      )}`;
    } else if (value.length > 3) {
      value = `+1 (${value.slice(0, 3)}) ${value.slice(3)}`;
    } else if (value.length > 0) {
      value = `+1 (${value}`;
    } else {
      value = "+1";
    }

    form?.setValue("number", value);
  };

  const onHandleClickLogin = async (event) => {
    event.preventDefault();
    close(Modal.Register);
    open(Modal.Login);
  };

  useEffect(() => {
    let interval;
    if (newUser) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) =>
          prevCountdown > 0 ? prevCountdown - 1 : 0
        );
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [newUser]);

  useEffect(() => {
    if (countdown === 0) {
      setShowCountDown(false);
    }
  }, [countdown]);

  const handleResendCode = async () => {
    try {
      await resendSignUpCode({ username: email });
      setShowCountDown(true);
      setCountdown(120);
    } catch (error) {
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false,
      });
    }
  };

  const onHandleSignUp = async (data) => {
    try {
      setLoading(true);
      const formattedPhoneNumber = data.number?.replace(/[^\d]/g, "");

      if (newUser) {
        const response = await confirmSignUp({
          username: email,
          confirmationCode: data?.prompt_code,
        });
        if (response?.isSignUpComplete) {
          const res = await autoSignIn();
          if (res) {
            console.log("res", res);
            await fetchFullUserProfile();

            navigate("/profile");
            setTimeout(() => {
              ModalStore.closeAll();
              ModalStore.openSimpleSuccess({
                fontSize: "40px",
                subTitleFont: "14px",
                title: "Almost There!",
                subtitle:
                  "Step 1 of 2: Fill in your address details to complete your account profile.",
                shouldCloseOnOverlayClick: false,
              });
              open(Modal.Profile);
            }, 1000);

            ModalStore.openSimpleSuccess({
              title: "SUCCESS!",
              subtitle: "Registration completed successfully",
            });
          } else {
            ModalStore.openSimpleSuccess({
              title: "SUCCESS!",
              subtitle: "Registration completed successfully. Please log in.",
            });
          }
          close(Modal.Register);
          setIsAction("SignUp");
        }
      } else {
        setEmail(data.email);

        const response = await signUp({
          username: data.email,
          password: data.password,
          options: {
            userAttributes: {
              email: data.email,
              given_name: data.given_name,
              family_name: data.family_name,
              phone_number: `+${formattedPhoneNumber}`,
              "custom:size": data.size,
            },
            autoSignIn: true,
          },
        });

        if (response?.nextStep?.signUpStep === "CONFIRM_SIGN_UP") {
          setNewUser(true);
          return;
        } else {
          ModalStore.openSimpleError({
            icon: undefined,
            title: "Error",
            subtitle: "Fail: Other confirmation methods",
            shouldCloseOnOverlayClick: false,
          });
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message || error?.toString() || "Undefined error",
        shouldCloseOnOverlayClick: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderConfirmationForm = () => {
    return (
      <ConfirmSignUpForm
        showCountDown={showCountDown}
        countdown={countdown}
        disabled={countdown > 0}
        onResend={handleResendCode}
        loading={loading}
        errors={errors}
        register={register}
      />
    );
  };

  const renderForm = () => {
    return (
      <div className="register-modal-content">
        <div className="register-content">
          <div className="register-content-form">
            <FormInputField
              name="given_name"
              label="First name"
              placeholder="Your full name"
              register={register}
              icon={<img src={UserIcon} alt="user" />}
              errors={errors}
            />
            <FormInputField
              name="family_name"
              label="Last name"
              placeholder="Your last name"
              register={register}
              icon={<img src={UserIcon} alt="user" />}
              errors={errors}
            />

            <div className="w-full">
              <MultiSelectField
                className="w-full"
                label="Seat Size"
                placeholder="Select Size"
                options={seatSizes}
                selectedValue={selectedSizes}
                onChange={(selectedItems) => {
                  const selectedValues = selectedItems
                    ? selectedItems.map((item) => item.value?.toString())
                    : [];
                  const sizeString = selectedValues.join(",");
                  form.setValue("size", sizeString, { shouldValidate: true });
                }}
              />
              {errors.size && (
                <p className="text-red-500 text-sm font-montserrat">
                  {errors.size.message}
                </p>
              )}
            </div>

            <FormInputField
              name="email"
              label="Email"
              placeholder="example@example.com"
              register={register}
              icon={<img src={MailIcon} alt="email" />}
              errors={errors}
            />

            <FormInputField
              name="number"
              label="Phone Number"
              placeholder="Your phone number "
              register={register}
              icon={<ClientPhoneIcon />}
              errors={errors}
              onChange={handlePhoneNumberChange}
            />

            <PasswordInputField
              name="password"
              label="Password"
              placeholder="Enter password"
              register={register}
              errors={errors}
              icon={<img src={LockIcon} alt="lock" />}
            />

            <PasswordInputField
              name="repeatpassword"
              label="Repeat password"
              placeholder="Repeat password"
              register={register}
              errors={errors}
              icon={<img src={LockIcon} alt="lock" />}
            />
          </div>
          <div className="register-content-submit mt-[16px]">
            <Button
              loading={loading}
              type="submit"
              className={"modal_form_btn"}
              disabled={loading}
              size="xl"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Join Now"
              )}
            </Button>
            {errors.prompt_code && (
              <p className="text-red-400 error-text">
                {errors.prompt_code.message}
              </p>
            )}
            <span className={"register-content-submit__text"}>
              Have an account already?{" "}
              <Link
                onClick={onHandleClickLogin}
                className={"font-bold underline text-primary"}
                to={"/user/horses"}
              >
                Log in
              </Link>
            </span>
          </div>
          {/* social media buttons */}
          <div className="register-content-links">
            <a
              // onClick={() => {
              //   if (location.pathname === "/") {
              //     close(Modal.Register);
              //     navigate("/terms-and-conditions");
              //     setTimeout(() => {
              //       scrollFunctions.scrollToSecFour();
              //     }, 0);
              //   } else {
              //     scrollFunctions.scrollToSecFour();
              //   }
              // }}
              onClick={() => {
                close(Modal.Register);
                navigate("/terms-and-conditions");
              }}
              className={"text-secondary cursor-pointer"}
            >
              Terms & Conditions
            </a>
            <a
              onClick={() => {
                close(Modal.Register);
                navigate("/privacy-policy");
              }}
              className={"text-secondary cursor-pointer"}
            >
              Privacy policy
            </a>
            {/* <a
              onClick={() => {
                if (location.pathname === "/") {
                  close(Modal.Register);
                  navigate("/partner");
                  setTimeout(() => {
                    scrollFunctions.scrollToSecFour(                  closeModal("isRegisterModalOpen");
   } else {
                  scrollFunctions.scrollToSecFour();
                }
              }}
              className={"text-secondary cursor-pointer"}
            >
              Contact us
            </a> */}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onHandleSignUp)}>
          {newUser === null ? renderForm() : renderConfirmationForm()}
        </form>
      </FormProvider>
    </>
  );
};

export default RegisterForm;

export const PasswordInputField = ({
  name,
  label,
  placeholder,
  register,
  errors,
  icon,
  ...props
}) => {
  return (
    <div>
      <PasswordInput
        label={label}
        placeholder={placeholder}
        {...register(name)}
        {...props}
        icon={icon}
      />
      <div className="h-2 ml-2">
        {errors?.[name] && (
          <p className="text-red-500 text-sm font-montserrat mt-1">
            {errors?.[name]?.message}
          </p>
        )}
      </div>
    </div>
  );
};

export const FormInputField = ({
  label,
  placeholder,
  name,
  register,
  errors,
  ...props
}) => {
  return (
    <div>
      <Input
        label={label}
        placeholder={placeholder}
        {...register(name)}
        {...props}
      />
      <div className="h-2 ml-2">
        {errors?.[name] && (
          <p className="text-red-500 text-sm font-montserrat mt-1">
            {errors?.[name]?.message}
          </p>
        )}
      </div>
    </div>
  );
};
